// ── Shared formatting utilities ─────────────────────────────────────────────
// Central home for number, percentage, currency, and date formatting.
// Every component should import from here instead of inlining .toFixed() etc.

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/**
 * Format a numeric value as USD currency.
 * @param {number|null|undefined} value
 * @param {{ compact?: boolean }} opts  — compact: "$1.2M" / "$45K"
 * @returns {string}
 */
export function formatCurrency(value, optsOrCompact = {}) {
  // Backward compat: formatCurrency(val, true) from old positional API
  const compact = typeof optsOrCompact === 'boolean'
    ? optsOrCompact
    : (optsOrCompact?.compact ?? false);
  if (value == null) return '—';
  if (compact && Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (compact && Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return USD.format(value);
}

/**
 * Format a 0–1 ratio as a percentage string.
 * @param {number|null|undefined} value — 0.85 → "85.0%"
 * @param {{ decimals?: number, alreadyPercent?: boolean }} opts
 * @returns {string}
 */
export function formatPercent(value, { decimals = 1, alreadyPercent = false } = {}) {
  if (value == null) return '—';
  const pct = alreadyPercent ? value : value * 100;
  return `${pct.toFixed(decimals)}%`;
}

/**
 * Format a number with locale grouping (e.g. 1,234).
 * @param {number|null|undefined} value
 * @param {{ decimals?: number, compact?: boolean }} opts
 * @returns {string}
 */
export function formatNumber(value, { decimals = 0, compact = false } = {}) {
  if (value == null) return '—';
  if (compact && Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (compact && Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(decimals > 0 ? decimals : 1)}K`;
  }
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a date value as a readable string.
 * @param {string|Date|null|undefined} value
 * @param {"short"|"medium"|"long"} format
 * @returns {string}
 */
export function formatDate(value, format = 'medium') {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '—';

  const opts = {
    short:  { month: 'short', day: 'numeric' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long:   { month: 'long',  day: 'numeric', year: 'numeric' },
  };
  return d.toLocaleDateString('en-US', opts[format] ?? opts.medium);
}

/**
 * Format a date+time value as a readable string (e.g. "Mar 25, 2:30 PM").
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export function formatDateTime(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Format an ISO date string as a relative time label.
 * "just now", "3m ago", "2h ago", "5d ago", or a short date for older.
 */
export function formatRelativeTime(isoString) {
  if (!isoString) return '';
  const now = Date.now();
  const then = new Date(isoString).getTime();
  if (isNaN(then)) return '';
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
